import { NamespacedIconName } from 'src/app/shared/constants/material-icon-name';
export type MasterDataSectionKey =
  | 'Product'
  | 'Commercial'
  | 'Organization'
  | 'Common'
  | 'Warehouse'
  | 'Logistics'
  | 'Finance'
  | 'Quality'
  | 'Risk';

export interface MasterDataEntity {
  section: MasterDataSectionKey;
  route: string;
  countKey: string;
  navLabelKey: string;
  cardLabelKey: string;
  iconOutline: NamespacedIconName;
}

export interface MasterDataSectionMeta {
  key: MasterDataSectionKey;
  navTitleKey: string;
  cardTitleKey: string;
  cardIcon: NamespacedIconName;
}

export const MASTER_DATA_SECTIONS: readonly MasterDataSectionMeta[] = [
  {
    key: 'Product',
    navTitleKey: 'Navigation.Sections.Product',
    cardTitleKey: 'MasterData.Sections.Product',
    cardIcon: 'inventory_2',
  },
  {
    key: 'Commercial',
    navTitleKey: 'Navigation.Sections.Commercial',
    cardTitleKey: 'MasterData.Sections.Commercial',
    cardIcon: 'storefront',
  },
  {
    key: 'Organization',
    navTitleKey: 'Navigation.Sections.Organization',
    cardTitleKey: 'MasterData.Sections.Organization',
    cardIcon: 'corporate_fare',
  },
  {
    key: 'Common',
    navTitleKey: 'Navigation.Sections.Common',
    cardTitleKey: 'MasterData.Sections.Common',
    cardIcon: 'public',
  },
  {
    key: 'Warehouse',
    navTitleKey: 'Navigation.Sections.Warehouse',
    cardTitleKey: 'MasterData.Sections.Warehouse',
    cardIcon: 'warehouse',
  },
  {
    key: 'Logistics',
    navTitleKey: 'Navigation.Sections.Logistics',
    cardTitleKey: 'MasterData.Sections.Logistics',
    cardIcon: 'local_shipping',
  },
  {
    key: 'Finance',
    navTitleKey: 'Navigation.Sections.Finance',
    cardTitleKey: 'MasterData.Sections.Finance',
    cardIcon: 'account_balance',
  },
  {
    key: 'Quality',
    navTitleKey: 'Navigation.Sections.Quality',
    cardTitleKey: 'MasterData.Sections.Quality',
    cardIcon: 'science',
  },
  {
    key: 'Risk',
    navTitleKey: 'Navigation.Sections.Risk',
    cardTitleKey: 'MasterData.Sections.Risk',
    cardIcon: 'warning',
  },
];

export const MASTER_DATA_TAXONOMY: readonly MasterDataEntity[] = [
  // Product
  {
    section: 'Product',
    route: 'item',
    countKey: 'item',
    navLabelKey: 'Navigation.Items',
    cardLabelKey: 'MasterData.Item',
    iconOutline: 'mat_outline:deployed_code',
  },
  {
    section: 'Product',
    route: 'brand',
    countKey: 'brand',
    navLabelKey: 'Navigation.Brands',
    cardLabelKey: 'MasterData.Brand',
    iconOutline: 'mat_outline:workspace_premium',
  },
  {
    section: 'Product',
    route: 'catalog',
    countKey: 'catalog',
    navLabelKey: 'Navigation.Catalogs',
    cardLabelKey: 'MasterData.Catalog',
    iconOutline: 'mat_outline:layers',
  },
  {
    section: 'Product',
    route: 'item-variant',
    countKey: 'itemVariant',
    navLabelKey: 'Navigation.ItemVariants',
    cardLabelKey: 'MasterData.ItemVariant',
    iconOutline: 'mat_outline:content_copy',
  },
  {
    section: 'Product',
    route: 'item-group',
    countKey: 'itemGroup',
    navLabelKey: 'Navigation.ItemGroups',
    cardLabelKey: 'MasterData.ItemGroup',
    iconOutline: 'mat_outline:inventory_2',
  },

  // Commercial
  {
    section: 'Commercial',
    route: 'commodity',
    countKey: 'commodity',
    navLabelKey: 'Navigation.Commodities',
    cardLabelKey: 'MasterData.Commodity',
    iconOutline: 'mat_outline:shopping_cart',
  },
  {
    section: 'Commercial',
    route: 'commodity-group',
    countKey: 'commodityGroup',
    navLabelKey: 'Navigation.CommodityGroups',
    cardLabelKey: 'MasterData.CommodityGroup',
    iconOutline: 'mat_outline:grid_view',
  },
  {
    section: 'Commercial',
    route: 'unit',
    countKey: 'unit',
    navLabelKey: 'Navigation.UnitOfMeasure',
    cardLabelKey: 'MasterData.UnitOfMeasure',
    iconOutline: 'mat_outline:straighten',
  },
  {
    section: 'Commercial',
    route: 'sales-channel',
    countKey: 'salesChannel',
    navLabelKey: 'Navigation.SalesChannelCatalog',
    cardLabelKey: 'MasterData.SalesChannel',
    iconOutline: 'mat_outline:storefront',
  },
  {
    section: 'Commercial',
    route: 'incoterm',
    countKey: 'incoterm',
    navLabelKey: 'Navigation.Incoterms',
    cardLabelKey: 'MasterData.Incoterm',
    iconOutline: 'mat_outline:description',
  },

  // Organization
  {
    section: 'Organization',
    route: 'business-unit',
    countKey: 'businessUnit',
    navLabelKey: 'Navigation.BusinessUnits',
    cardLabelKey: 'MasterData.BusinessUnit',
    iconOutline: 'mat_outline:apartment',
  },
  {
    section: 'Organization',
    route: 'business-type',
    countKey: 'businessType',
    navLabelKey: 'Navigation.BusinessTypes',
    cardLabelKey: 'MasterData.BusinessType',
    iconOutline: 'mat_outline:business_center',
  },
  {
    section: 'Organization',
    route: 'business-group',
    countKey: 'businessGroup',
    navLabelKey: 'Navigation.BusinessGroups',
    cardLabelKey: 'MasterData.BusinessGroup',
    iconOutline: 'mat_outline:grid_view',
  },
  {
    section: 'Organization',
    route: 'department',
    countKey: 'department',
    navLabelKey: 'Navigation.Departments',
    cardLabelKey: 'MasterData.Department',
    iconOutline: 'mat_outline:groups',
  },
  {
    section: 'Organization',
    route: 'position',
    countKey: 'position',
    navLabelKey: 'Navigation.Positions',
    cardLabelKey: 'MasterData.Position',
    iconOutline: 'mat_outline:account_tree',
  },
  {
    section: 'Organization',
    route: 'office',
    countKey: 'office',
    navLabelKey: 'Navigation.Offices',
    cardLabelKey: 'MasterData.Office',
    iconOutline: 'mat_outline:domain',
  },
  {
    section: 'Organization',
    route: 'point-of-sale',
    countKey: 'pointOfSale',
    navLabelKey: 'Navigation.PointOfSale',
    cardLabelKey: 'MasterData.PointOfSale',
    iconOutline: 'mat_outline:storefront',
  },
  {
    // "Đơn vị bán" = pháp nhân/cửa hàng bán (Contact isOwner). Backs order Order.SellerId.
    section: 'Organization',
    route: 'seller-unit',
    countKey: 'sellerUnit',
    navLabelKey: 'Navigation.SellerUnits',
    cardLabelKey: 'MasterData.SellerUnit',
    iconOutline: 'mat_outline:badge',
  },
  {
    section: 'Organization',
    route: 'rank',
    countKey: 'rank',
    navLabelKey: 'Navigation.Ranks',
    cardLabelKey: 'MasterData.Rank',
    iconOutline: 'mat_outline:military_tech',
  },

  // Common
  {
    section: 'Common',
    route: 'country',
    countKey: 'country',
    navLabelKey: 'Navigation.Countries',
    cardLabelKey: 'MasterData.Country',
    iconOutline: 'mat_outline:public',
  },
  {
    section: 'Common',
    route: 'region',
    countKey: 'region',
    navLabelKey: 'Navigation.Regions',
    cardLabelKey: 'MasterData.Region',
    iconOutline: 'mat_outline:map',
  },
  {
    section: 'Common',
    route: 'address',
    countKey: 'address',
    navLabelKey: 'Navigation.Addresses',
    cardLabelKey: 'MasterData.Address',
    iconOutline: 'mat_outline:location_on',
  },
  {
    section: 'Common',
    route: 'currency',
    countKey: 'currency',
    navLabelKey: 'Navigation.Currencies',
    cardLabelKey: 'MasterData.Currency',
    iconOutline: 'mat_outline:toll',
  },
  {
    section: 'Common',
    route: 'bank',
    countKey: 'bank',
    navLabelKey: 'Navigation.Banks',
    cardLabelKey: 'MasterData.Bank',
    iconOutline: 'mat_outline:account_balance',
  },
  {
    section: 'Common',
    route: 'bank-account',
    countKey: 'bankAccount',
    navLabelKey: 'Navigation.BankAccounts',
    cardLabelKey: 'MasterData.BankAccount',
    iconOutline: 'mat_outline:credit_card',
  },
  {
    section: 'Common',
    route: 'reason',
    countKey: 'reason',
    navLabelKey: 'Navigation.Reasons',
    cardLabelKey: 'MasterData.Reason',
    iconOutline: 'mat_outline:description',
  },

  // Warehouse
  {
    section: 'Warehouse',
    route: 'warehouse',
    countKey: 'warehouse',
    navLabelKey: 'Navigation.Warehouses',
    cardLabelKey: 'MasterData.Warehouse',
    iconOutline: 'mat_outline:warehouse',
  },
  {
    section: 'Warehouse',
    route: 'warehouse-type',
    countKey: 'warehouseType',
    navLabelKey: 'Navigation.WarehouseTypes',
    cardLabelKey: 'MasterData.WarehouseType',
    iconOutline: 'mat_outline:layers',
  },
  {
    section: 'Warehouse',
    route: 'location',
    countKey: 'location',
    navLabelKey: 'Navigation.Locations',
    cardLabelKey: 'MasterData.Location',
    iconOutline: 'mat_outline:location_on',
  },
  {
    section: 'Warehouse',
    route: 'location-type',
    countKey: 'locationType',
    navLabelKey: 'Navigation.LocationTypes',
    cardLabelKey: 'MasterData.LocationType',
    iconOutline: 'mat_outline:grid_view',
  },
  {
    section: 'Warehouse',
    route: 'location-profile',
    countKey: 'locationProfile',
    navLabelKey: 'Navigation.LocationProfiles',
    cardLabelKey: 'MasterData.LocationProfile',
    iconOutline: 'mat_outline:description',
  },
  {
    section: 'Warehouse',
    route: 'bin-location',
    countKey: 'binLocation',
    navLabelKey: 'Navigation.BinLocations',
    cardLabelKey: 'MasterData.BinLocation',
    iconOutline: 'mat_outline:inventory_2',
  },
  {
    section: 'Warehouse',
    route: 'zone',
    countKey: 'zone',
    navLabelKey: 'Navigation.Zones',
    cardLabelKey: 'MasterData.Zone',
    iconOutline: 'mat_outline:map',
  },
  {
    section: 'Warehouse',
    route: 'zone-group',
    countKey: 'zoneGroup',
    navLabelKey: 'Navigation.ZoneGroups',
    cardLabelKey: 'MasterData.ZoneGroup',
    iconOutline: 'mat_outline:account_tree',
  },

  // Logistics
  {
    section: 'Logistics',
    route: 'transportation',
    countKey: 'transportation',
    navLabelKey: 'Navigation.Transportation',
    cardLabelKey: 'MasterData.Transportation',
    iconOutline: 'mat_outline:local_shipping',
  },
  {
    section: 'Logistics',
    route: 'package-type',
    countKey: 'packageType',
    navLabelKey: 'Navigation.PackageTypes',
    cardLabelKey: 'MasterData.PackageType',
    iconOutline: 'mat_outline:inventory_2',
  },
  {
    section: 'Logistics',
    route: 'port',
    countKey: 'port',
    navLabelKey: 'Navigation.Ports',
    cardLabelKey: 'MasterData.Port',
    iconOutline: 'mat_outline:anchor',
  },
  {
    section: 'Logistics',
    route: 'vehicle',
    countKey: 'vehicle',
    navLabelKey: 'Navigation.Vehicles',
    cardLabelKey: 'MasterData.Vehicle',
    iconOutline: 'mat_outline:directions_car',
  },
  {
    section: 'Logistics',
    route: 'charge-group',
    countKey: 'chargeGroup',
    navLabelKey: 'Navigation.ChargeGroups',
    cardLabelKey: 'MasterData.ChargeGroup',
    iconOutline: 'mat_outline:grid_view',
  },
  {
    section: 'Logistics',
    route: 'charge-measurement',
    countKey: 'chargeMeasurement',
    navLabelKey: 'Navigation.ChargeMeasurements',
    cardLabelKey: 'MasterData.ChargeMeasurement',
    iconOutline: 'mat_outline:straighten',
  },
  {
    section: 'Logistics',
    route: 'charge-type',
    countKey: 'chargeType',
    navLabelKey: 'Navigation.ChargeTypes',
    cardLabelKey: 'MasterData.ChargeType',
    iconOutline: 'mat_outline:toll',
  },

  // Finance
  {
    section: 'Finance',
    route: 'chart-of-account',
    countKey: 'chartOfAccount',
    navLabelKey: 'Navigation.ChartOfAccounts',
    cardLabelKey: 'MasterData.ChartOfAccounts',
    iconOutline: 'mat_outline:account_balance',
  },
  {
    section: 'Finance',
    route: 'tax-and-charge',
    countKey: 'taxAndCharge',
    navLabelKey: 'Navigation.TaxAndCharges',
    cardLabelKey: 'MasterData.TaxCharge',
    iconOutline: 'mat_outline:percent',
  },
  {
    section: 'Finance',
    route: 'tax-and-charge-category',
    countKey: 'taxAndChargeCategory',
    navLabelKey: 'Navigation.TaxAndChargeCategories',
    cardLabelKey: 'MasterData.TaxChargeCategory',
    iconOutline: 'mat_outline:grid_view',
  },
  {
    section: 'Finance',
    route: 'payment-term',
    countKey: 'paymentTerm',
    navLabelKey: 'Navigation.PaymentTerms',
    cardLabelKey: 'MasterData.PaymentTerm',
    iconOutline: 'mat_outline:description',
  },
  {
    section: 'Finance',
    route: 'income-expense-category',
    countKey: 'incomeExpenseCategory',
    navLabelKey: 'Navigation.IncomeExpenseCategories',
    cardLabelKey: 'MasterData.IncomeExpenseCategory',
    iconOutline: 'mat_outline:toll',
  },

  // Quality
  {
    section: 'Quality',
    route: 'inspection-parameter',
    countKey: 'inspectionParameter',
    navLabelKey: 'Navigation.InspectionParameters',
    cardLabelKey: 'MasterData.InspectionParameter',
    iconOutline: 'mat_outline:science',
  },
  {
    section: 'Quality',
    route: 'inspection-parameter-group',
    countKey: 'inspectionParameterGroup',
    navLabelKey: 'Navigation.InspectionParameterGroups',
    cardLabelKey: 'MasterData.InspectionParamGroup',
    iconOutline: 'mat_outline:grid_view',
  },

  // Risk
  {
    section: 'Risk',
    route: 'risk-category',
    countKey: 'riskCategory',
    navLabelKey: 'Navigation.RiskCategories',
    cardLabelKey: 'MasterData.RiskCategory',
    iconOutline: 'mat_outline:warning',
  },
];
